import JobModel from '@/common/models/jobs/job-model';
import DataExportJobModel from '@/common/models/jobs/data-export-job-model';
import BatchRefundJobModel from '@/common/models/jobs/batch-refund-job-model';
import timezone from '@/common/timezone';

const hydrateJob = (job, api) => {
    switch (job.type) {
        case JobModel.Types.dataExport:
            return new DataExportJobModel(job);
        case JobModel.Types.batchRefund:
            return new BatchRefundJobModel({...job, api});
        default:
            return new JobModel(job);
    }
};

const JobStatuses = Object.values(JobModel.Statuses);

export default ({api}) => ({
    namespaced: true,

    state: {
        pending: [],
        running: [],
        completed: [],
        lastCheckTime: null,
    },
    getters: {
        getPending: state => state.pending.map(job => hydrateJob(job, api)),
        getRunning: state => state.running.map(job => hydrateJob(job, api)),
        getCompleted: state => state.completed
            .map(job => hydrateJob(job, api))
            .sort((dateA, dateB) => dateB.jobTime.valueOf() - dateA.jobTime.valueOf()),
        getLastCheckTime: state => timezone.readUTC(state.lastCheckTime) || timezone.now(),
        findJob: state => (id) => {
            let foundJob;
            // using find as a lazy forEach
            JobStatuses.find((status) => {
                state[status].findIndex((job, index) => {
                    if (job.id === id) {
                        foundJob = {index, status, job};
                        return true;
                    }
                    return false;
                });
                return false;
            });
            return foundJob;
        },
    },
    mutations: {
        addJob(state, {job, to}) {
            if (!to) {
                state[JobModel.Statuses.pending].push(job.getPayload());
            } else {
                state[to].push(job.getPayload());
            }
        },
        removeJob(state, {status, index}) {
            state[status].splice(index, 1);
        },
        moveJob(state, {
            status, index, job, to,
        }) {
            if (job) {
                const jobClone = {...job};
                state[status].splice(index, 1);
                if (!to) {
                    const lastStatus = JobStatuses[JobStatuses.length - 1];
                    const statusIndex = JobStatuses.findIndex(item => item === status);
                    if (JobStatuses[statusIndex] !== lastStatus) {
                        state[JobStatuses[statusIndex + 1]].push(jobClone);
                    } else {
                        state[status].push(jobClone);
                    }
                } else {
                    state[to].push(jobClone);
                }
            }
        },
        clearJobs(state, {
            all, status, type, location,
        }) {
            if (all) {
                state.pending = [];
                state.running = [];
                state.completed = [];
            } else if (status) {
                state[status] = [];
            } else if (type) {
                JobStatuses.forEach((item) => {
                    state[item] = state[item].filter(job => job.type !== type);
                });
            } else if (location) {
                JobStatuses.forEach((item) => {
                    state[item] = state[item].filter(job => job.location !== location);
                });
            }
        },
        updateLastCheckTime(state) {
            state.lastCheckTime = timezone.getUTC();
        },
    },
    actions: {
        async addJob({commit, dispatch}, {job, to}) {
            if (job.isFrontend) {
                commit('addJob', {job, to: JobModel.Statuses.running});
                await job.start();
                dispatch('moveJob', {id: job.id, to: JobModel.Statuses.completed});
            } else if (job.isBackend) {
                commit('addJob', {job, to});
            }
        },
        async removeJob({commit, getters}, id) {
            try {
                const {status, index} = getters.findJob(id);
                commit('removeJob', {status, index});
            } catch (error) {
                throw new Error('Job was not found');
            }
        },
        async moveJob({commit, getters}, {id, to}) {
            try {
                const {status, index, job} = getters.findJob(id);
                commit('moveJob', {
                    status, index, job, to,
                });
            } catch (error) {
                throw new Error('Could not move job');
            }
        },
        async forceRestartJob({dispatch}, {job}) {
            if (job.isFrontend) {
                dispatch('removeJob', job.id);
                dispatch('addJob', {job});
            }
        },
        clearJobs({commit}, args) {
            commit('clearJobs', args);
        },
        updateLastCheckTime({commit}) {
            commit('updateLastCheckTime');
        },
        async resumeFrontendJobs({dispatch, getters}) {
            getters.getRunning
                .filter(job => job.isFrontend)
                .forEach(async (job) => {
                    await job.start();
                    dispatch('moveJob', {id: job.id, to: JobModel.Statuses.completed});
                });
        },
        async pollBackendJobs({dispatch, rootGetters}) {
            try {
                const [
                    dataExportJobs,
                ] = await Promise.all([
                    // call all the backend job methods here
                    DataExportJobModel.pollJobs(api, rootGetters),
                ]);
                dispatch('clearJobs', {location: JobModel.Locations.backend});
                // push all the jobs to the queue here
                dataExportJobs.completed.forEach((job) => {
                    dispatch('addJob', {job, to: JobModel.Statuses.completed});
                });
                dataExportJobs.running.forEach((job) => {
                    dispatch('addJob', {job, to: JobModel.Statuses.running});
                });
            } catch (error) {
                throw new Error(error);
            }
        },
    },
});
